import React, { useState, useEffect, useMemo } from 'react';
import { MetricsResumenTab } from './metrics/MetricsResumenTab';
import { MetricsAsignacionTab } from './metrics/MetricsAsignacionTab';
import { MetricsAsignacionNTab } from './metrics/MetricsAsignacionNTab';
import { MetricsLiquidacionTab } from './metrics/MetricsLiquidacionTab';
import { MetricsInactividadTab } from './metrics/MetricsInactividadTab';
import { MetricsPsiPlusTab } from './metrics/MetricsPsiPlusTab';
import { MetricsCaminoInicialTab } from './metrics/MetricsCaminoInicialTab';
import { MetricsBloqueosTab } from './metrics/MetricsBloqueosTab';
import { getPsychologistMetrics, PsychologistMetrics } from '../../../hooks/api/psychologist/metrics';
import { usePsychologistContext } from '../../../contexts/PsychologistContext';
import { useAuth } from '../../../contexts/AuthContext';
import apiCoreAdapter from '../../../hooks/apiCoreAdapter';
import CircularProgress from '../../common/CircularProgress';

const RANKING_WEBHOOK_URL = 'https://hook.us1.make.com/wbr01ud7bvw6nubj50477jiscm56810w';

type MetricsSubTab = 'resumen' | 'asignacion' | 'asignacion-inicial' | 'psi-plus' | 'liquidacion' | 'bloqueos' | 'inactividad' | 'camino-inicial';

interface ProfileMetricsTabProps {
  /** Bloqueo de calendario activo (Tope / Sin horarios / Situación personal) — mismo estado que
   *  ya usa el banner "Bloqueada para recibir pacientes nuevos" en PsychologistProfile.tsx. */
  activeBlockade: { reason: string; to: string | null } | false | undefined;
  /** Bloqueo por vacaciones/días off activo — mismo estado que ya usa el banner de vacaciones. */
  activeVacationEnd: string | null | false | undefined;
  processLimitPrefered: number | null;
  /** Navega a la pestaña "Vacaciones y Días off" (tab de nivel superior en PsychologistProfile). */
  onGoToVacationsTab: () => void;
  /** Abre el modal "¿Qué pasa con la agenda para pacientes nuevos?" (mismo modal del banner de vacaciones). */
  onOpenVacationBlockInfo: () => void;
}

// Status reales de `psychologist_status` (verificado por Metabase): active, blocked,
// pending discharged, discharged.
const DISCHARGE_STATUS_NAMES = ['pending discharged', 'discharged'];

// "Desempeño" se saca por completo para todas las psicólogas (sin importar ranking/estado):
// nunca tuvo un contenido implementado detrás, quedaba en blanco al entrar.
const SUB_TABS: { id: MetricsSubTab; label: string }[] = [
  { id: 'resumen', label: 'Estadísticas' },
  { id: 'inactividad', label: 'Tasa de inactividad' },
  { id: 'camino-inicial', label: 'Camino inicial' },
  { id: 'asignacion', label: 'Asignaciones Generales' },
  { id: 'asignacion-inicial', label: 'Asignaciones Iniciales' },
  { id: 'psi-plus', label: 'Programa PSI+' },
  { id: 'liquidacion', label: 'Liquidación' },
  { id: 'bloqueos', label: 'Bloqueos' },
];

function egresoAckKey(psychologistId: string) {
  return `psi-journey-egreso-ack-${psychologistId}`;
}

// Tabs que recién se vuelven visibles al graduarse de NZ (antes el journey las tapaba con
// "Camino inicial"). Se marcan con badge "Nuevo" hasta que la psicóloga entra a cada una.
const NUEVO_BADGE_TABS: MetricsSubTab[] = ['asignacion', 'inactividad', 'psi-plus'];

function nuevoTabBadgeSeenKey(psychologistId: string, tab: MetricsSubTab) {
  return `psi-metrics-tab-nuevo-badge-seen-${psychologistId}-${tab}`;
}

export const ProfileMetricsTab: React.FC<ProfileMetricsTabProps> = ({
  activeBlockade,
  activeVacationEnd,
  processLimitPrefered,
  onGoToVacationsTab,
  onOpenVacationBlockInfo,
}) => {
  const [activeTab, setActiveTab] = useState<MetricsSubTab>('resumen');
  const [metrics, setMetrics] = useState<PsychologistMetrics | null>(null);
  const [loading, setLoading] = useState(true);
  const [blockades, setBlockades] = useState<Array<{ active: boolean; reason: { name: string } | null }>>([]);
  const [rankingActual, setRankingActual] = useState<string | null>(null);
  const [rankingAnterior, setRankingAnterior] = useState<string | null>(null);
  const [rankingLoading, setRankingLoading] = useState(true);
  const [rankingError, setRankingError] = useState(false);
  const [metricsError, setMetricsError] = useState(false);
  const { psychologist } = usePsychologistContext();
  const { psychologistId } = useAuth();

  const resolvedPsychologistId = psychologistId || psychologist?.id || '';
  const everHadProcesses = (metrics?.active_processes ?? 0) + (metrics?.inactive_processes ?? 0) > 0;
  const isDischargeStatusReal = DISCHARGE_STATUS_NAMES.includes((psychologist?.statusName ?? '').toLowerCase());
  const egresoAck = resolvedPsychologistId ? localStorage.getItem(egresoAckKey(resolvedPsychologistId)) === 'true' : false;

  // "Tu estadía en Psi Mammoliti" para NZ colapsa a 2 tabs (Camino inicial + Liquidación) en 3
  // situaciones, sin ningún endpoint de journey propio — todo inferido de datos que ya existen:
  // - NZ activa (ranking_category = 'NZ') → journey normal.
  // - NZ que le va mal y la dan de baja: pasa a ranking O + status pending discharged/discharged.
  //   Si tuvo procesos alguna vez → card de "Pending discharge" (no va a recibir asignaciones
  //   nuevas). Si nunca tuvo procesos, ya la expulsó AppShell antes de llegar a Perfil. Esta
  //   transición es definitiva — nunca "gradúa" ni hay popup de egreso — pero eso ya lo cubre el
  //   `!isDischargeStatusReal` de abajo (pasar a O desde NZ viene siempre acoplado a ese status).
  // - Recién graduada de NZ a Plantilla (ranking_anterior era 'NZ', ranking_actual ya no) y
  //   todavía no confirmó la card de Egreso → se le sigue mostrando el journey (con la card
  //   verde de egreso) hasta que confirma el popup; ahí se marca en localStorage y desde la
  //   próxima vez que entre ve las tabs normales (con badges "Nuevo" en las que recién se
  //   destapan: Asignaciones Generales, Tasa de Inactividad, Programa PSI+ y Home).
  const isJustGraduatedReal = rankingAnterior === 'NZ' && rankingActual !== 'NZ' && !isDischargeStatusReal && !egresoAck;

  // El status discharge/pending-discharge no es exclusivo de NZ: cualquier O puede pasar a baja
  // por otros motivos (inactividad, calidad, etc.) sin haber sido nunca NZ. Sin este chequeo, esa
  // O caía igual en la rama de journey NZ y veía Camino Inicial + el esquema de pagos de la Carta
  // Oferta NZ sin corresponderle.
  const wasEverNZ = rankingActual === 'NZ' || rankingAnterior === 'NZ';

  // `journeyMode` para el caso "pending-discharge" depende de `everHadProcesses`, que a su vez
  // depende de `metrics` (fetch separado del de ranking). Si solo esperáramos a `rankingLoading`,
  // una psicóloga en discharge que sí tuvo procesos vería primero las 4 tabs normales (con
  // `metrics` todavía en null → `everHadProcesses=false` → `journeyMode='none'`) y recién después
  // el colapso a Camino inicial cuando `metrics` resuelve — un flash confuso. Se espera también a
  // `loading` (metrics) en ese caso puntual, sin afectar a la mayoría de las psicólogas.
  const journeyModeLoading = rankingLoading || (isDischargeStatusReal && loading);

  type JourneyMode = 'active' | 'pending-discharge' | 'graduated' | 'none';
  const journeyMode: JourneyMode = isDischargeStatusReal
    ? (wasEverNZ && everHadProcesses ? 'pending-discharge' : 'none')
    : rankingActual === 'NZ'
      ? 'active'
      : isJustGraduatedReal
        ? 'graduated'
        : 'none';

  const showCaminoInicial = journeyMode !== 'none';
  const isDischargeStatus = journeyMode === 'pending-discharge';
  const isGraduated = journeyMode === 'graduated';

  // Mostrar solo Asignaciones Iniciales (fuera del journey NZ) si: ranking O con anterior N, o
  // ranking N — el caso NZ ya está cubierto por `showCaminoInicial` arriba.
  const isNuevaEnO = (rankingActual === 'O' && rankingAnterior === 'N') || rankingActual === 'N';

  // "Estadísticas" solo para quien no es N (isNuevaEnO). El caso NZ / O-desde-NZ ya está
  // cubierto por `showCaminoInicial` arriba, así que nunca llega a este branch.
  const hideEstadisticas = isNuevaEnO;

  const visibleTabs = useMemo(() => {
    if (showCaminoInicial) {
      return SUB_TABS.filter((tab) => tab.id === 'camino-inicial' || tab.id === 'liquidacion');
    }
    return SUB_TABS.filter((tab) => {
      if (tab.id === 'resumen' && hideEstadisticas) return false;
      if (tab.id === 'camino-inicial') return false;
      if (isNuevaEnO) {
        return !['asignacion', 'psi-plus', 'inactividad'].includes(tab.id);
      }
      return tab.id !== 'asignacion-inicial';
    });
  }, [isNuevaEnO, showCaminoInicial, hideEstadisticas]);

  useEffect(() => {
    let cancelled = false;

    setLoading(true);
    setMetricsError(false);
    getPsychologistMetrics()
      .then((res) => { if (!cancelled) setMetrics(res); })
      .catch(() => {
        if (cancelled) return;
        setMetrics(null);
        setMetricsError(true);
      })
      .finally(() => { if (!cancelled) setLoading(false); });

    apiCoreAdapter.psychologistBlockades()
      .then((res: { results: Array<{ active: boolean; reason: { name: string } | null }> }) => { if (!cancelled) setBlockades(res.results ?? []); })
      .catch(() => { if (!cancelled) setBlockades([]); });

    const psiId = psychologistId || psychologist?.id;
    if (!psiId) {
      setRankingLoading(false);
      return () => { cancelled = true; };
    }
    setRankingLoading(true);
    setRankingError(false);
    fetch(`${RANKING_WEBHOOK_URL}?psychologist_id=${psiId}`)
      .then((r) => r.json())
      .then((data: { ranking_actual: string; ranking_anterior: string }) => {
        if (cancelled) return;
        setRankingActual(data.ranking_actual);
        setRankingAnterior(data.ranking_anterior);
      })
      .catch((err) => {
        if (cancelled) return;
        console.error('No se pudo obtener ranking_actual/ranking_anterior de la psicóloga:', err);
        setRankingError(true);
      })
      .finally(() => { if (!cancelled) setRankingLoading(false); });

    return () => { cancelled = true; };
  }, [psychologistId, psychologist?.id]);

  // Si la tab activa queda oculta tras cargar el ranking, resetear al default correspondiente:
  // NZ (journey activo/pending-discharge/graduada) entra directo a "Camino inicial"; el resto
  // entra a "Resumen" si está disponible, o si no a la primera tab visible (ej. quien tiene
  // "Estadísticas" oculta entra directo a "Asignaciones Generales").
  useEffect(() => {
    if (!visibleTabs.find((t) => t.id === activeTab)) {
      if (showCaminoInicial) {
        setActiveTab('camino-inicial');
      } else if (visibleTabs.find((t) => t.id === 'resumen')) {
        setActiveTab('resumen');
      } else if (visibleTabs[0]) {
        setActiveTab(visibleTabs[0].id);
      }
    }
  }, [visibleTabs, activeTab, showCaminoInicial]);

  // Badge "Nuevo" en Asignaciones Generales / Tasa de Inactividad / Programa PSI+ / Desempeño:
  // solo para quien se graduó de NZ (confirmó el popup de egreso), y solo hasta que entra a
  // cada una por separado.
  const [seenNuevoTabs, setSeenNuevoTabs] = useState<Set<MetricsSubTab>>(() => {
    if (!resolvedPsychologistId) return new Set();
    return new Set(NUEVO_BADGE_TABS.filter((t) => localStorage.getItem(nuevoTabBadgeSeenKey(resolvedPsychologistId, t)) === 'true'));
  });

  useEffect(() => {
    if (!resolvedPsychologistId) return;
    setSeenNuevoTabs(new Set(NUEVO_BADGE_TABS.filter((t) => localStorage.getItem(nuevoTabBadgeSeenKey(resolvedPsychologistId, t)) === 'true')));
  }, [resolvedPsychologistId]);

  useEffect(() => {
    if (!resolvedPsychologistId || !egresoAck) return;
    if (!NUEVO_BADGE_TABS.includes(activeTab) || seenNuevoTabs.has(activeTab)) return;
    localStorage.setItem(nuevoTabBadgeSeenKey(resolvedPsychologistId, activeTab), 'true');
    setSeenNuevoTabs((prev) => new Set(prev).add(activeTab));
  }, [activeTab, egresoAck, resolvedPsychologistId, seenNuevoTabs]);

  return (
    <div className="flex flex-col gap-4">
      {rankingError && (
        <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
          No pudimos verificar tu categoría de ranking. Si no ves &quot;Camino inicial&quot; y deberías, recargá la página.
        </div>
      )}
      {journeyModeLoading ? (
        <div className="flex justify-center py-10">
          <CircularProgress label="Cargando tu perfil..." />
        </div>
      ) : (
      <>
      <div className="flex gap-1 bg-gray-100 rounded-xl p-1 overflow-x-auto">
        {visibleTabs.map((tab) => (
          <button
            key={tab.id}
            type="button"
            onClick={() => setActiveTab(tab.id)}
            className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium transition-all whitespace-nowrap ${
              activeTab === tab.id
                ? 'bg-violet-600 text-white shadow-sm'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            {tab.label}
            {egresoAck && NUEVO_BADGE_TABS.includes(tab.id) && !seenNuevoTabs.has(tab.id) && (
              <span className="ml-1.5 rounded-full bg-emerald-500 px-1.5 py-0.5 text-[10px] font-bold leading-none text-white align-middle">
                Nuevo
              </span>
            )}
          </button>
        ))}
      </div>

      {activeTab === 'resumen' && <MetricsResumenTab metrics={metrics} loading={loading} />}
      {activeTab === 'asignacion' && (
        <MetricsAsignacionTab
          metrics={metrics}
          loading={loading}
          onGoToTasaInactividad={() => setActiveTab('inactividad')}
          blockades={blockades}
        />
      )}
      {activeTab === 'asignacion-inicial' && <MetricsAsignacionNTab metrics={metrics} loading={loading} />}
      {activeTab === 'psi-plus' && (
        <MetricsPsiPlusTab
          metrics={metrics}
          loading={loading}
          onNavigateToInactividad={() => setActiveTab('inactividad')}
        />
      )}
      {activeTab === 'liquidacion' && <MetricsLiquidacionTab isNZ={showCaminoInicial} />}
      {activeTab === 'bloqueos' && (
        <MetricsBloqueosTab
          activeBlockade={activeBlockade}
          activeVacationEnd={activeVacationEnd}
          processLimitPrefered={processLimitPrefered}
          blockades={blockades}
          onGoToVacationsTab={onGoToVacationsTab}
          onOpenVacationBlockInfo={onOpenVacationBlockInfo}
        />
      )}
      {activeTab === 'inactividad' && !isNuevaEnO && <MetricsInactividadTab onGoToAsignacion={() => setActiveTab('asignacion')} />}
      {activeTab === 'camino-inicial' && showCaminoInicial && (
        <MetricsCaminoInicialTab
          metrics={metrics}
          loading={loading}
          metricsError={metricsError}
          psychologistId={resolvedPsychologistId}
          isDischargeStatus={isDischargeStatus}
          isGraduated={isGraduated}
          onGoToLiquidacion={() => setActiveTab('liquidacion')}
        />
      )}
      </>
      )}
    </div>
  );
};

export default ProfileMetricsTab;
